<template>
  <div class="document-manager fill-height">
    <!-- Mobile Drawer -->
    <v-navigation-drawer dark v-model="drawer" fixed temporary width="340" class="workspace-drawer" overlay-color="#080909" :overlay-opacity="0.62">
      <div class="drawer-heading">
        <div class="sidebar-title-wrap">
          <div class="sidebar-title-icon"><v-icon small color="white">mdi-file-tree-outline</v-icon></div>
          <div>
            <div class="sidebar-eyebrow">Navigation</div>
            <div class="sidebar-title">Ordner & Bereiche</div>
          </div>
        </div>
        <v-btn icon aria-label="Ordnernavigation schliessen" @click="drawer = false">
          <v-icon color="grey lighten-1">mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="drawer-content scroll-y">
        <v-select
          dark
          v-if="userService.hasRight('d_fetchAllDocs')"
          v-model="selectedStudioId"
          :items="mappedStudios"
          item-text="name"
          item-value="_id"
          label="Studio auswählen"
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-map-marker-outline"
          class="studio-select mb-5"
          @change="fetchDocuments(selectedFolder ? selectedFolder._id : null)"></v-select>

        <folder-tree
          :items="folderTreeWithInbox"
          :openNodes.sync="openNodes"
          :selected-folder-id="activeFolderId"
          @create-folder="openCreateFolderDialog"
          @select-folder="onFolderSelect"
          @create-subfolder="openCreateSubfolderDialog"
          @edit-folder="openEditFolderDialog"
          @delete-folder="openDeleteFolderDialog"
          @folder-drop="onFolderDrop"
          @drag-enter="handleDragEnter"
          @drag-leave="handleDragLeave" />
      </div>
    </v-navigation-drawer>

    <v-row class="manager-grid fill-height" no-gutters>
      <!-- Desktop Sidebar -->
      <v-col md="3" lg="3" class="desktop-sidebar d-none d-md-flex flex-column">
        <div class="sidebar-content fill-height scroll-y">
          <div class="sidebar-heading">
            <div class="sidebar-title-wrap">
              <div class="sidebar-title-icon"><v-icon small color="white">mdi-file-tree-outline</v-icon></div>
              <div>
                <div class="sidebar-eyebrow">Navigation</div>
                <div class="sidebar-title">Arbeitsbereich</div>
              </div>
            </div>
          </div>

          <v-select
            v-if="userService.hasRight('d_fetchAllDocs')"
            v-model="selectedStudioId"
            :items="mappedStudios"
            item-text="name"
            dark
            item-value="_id"
            label="Studio auswählen"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-map-marker-outline"
            class="studio-select mb-4"
            @change="fetchDocuments(selectedFolder ? selectedFolder._id : null)"></v-select>

          <folder-tree
            :items="folderTreeWithInbox"
            :openNodes.sync="openNodes"
            :selected-folder-id="activeFolderId"
            @create-folder="openCreateFolderDialog"
            @select-folder="onFolderSelect"
            @create-subfolder="openCreateSubfolderDialog"
            @edit-folder="openEditFolderDialog"
            @delete-folder="openDeleteFolderDialog"
            @folder-drop="onFolderDrop"
            @drag-enter="handleDragEnter"
            @drag-leave="handleDragLeave" />

          <div class="sidebar-hint">
            <v-icon small color="#8f9095">mdi-cursor-move</v-icon>
            <span>Dokumente per Drag & Drop in einen Ordner verschieben.</span>
          </div>
        </div>
      </v-col>

      <!-- Main Content: Dokumente -->
      <v-col cols="12" md="9" lg="9" class="document-content fill-height">
        <document-list
          :loading="loading"
          :documents="documents"
          :headers="documentHeaders"
          :folderName="searchQuery.trim() !== '' ? 'Suche' : selectedFolder && selectedFolder._id !== 'inbox' ? selectedFolder.name : 'Eingang'"
          :searchQuery="searchQuery"
          @search-documents="searchDocuments"
          @open-document-actions="openDocumentActionsDialog"
          @download-document="downloadDocument"
          @document-drag-start="onDocumentDragStart"
          @update:searchQuery="searchQuery = $event" />
      </v-col>
    </v-row>

    <!-- Dialog: Create/Edit Folder -->
    <folder-dialog
      :dialog="folderDialog"
      :mode="folderDialogMode"
      :folderData="folderForm"
      :parentOptions="parentFolderOptions"
      @save-folder="saveFolder"
      @close="closeFolderDialog" />

    <!-- Dialog: Confirm Delete Folder -->
    <delete-folder-dialog
      :dialog="deleteFolderDialog"
      :folder="folderToDelete"
      @confirm-delete="deleteFolder"
      @cancel-delete="deleteFolderDialog = false" />

    <!-- Dialog: Dokument bearbeiten -->
    <document-dialog
      :dialog="documentDialog"
      :documentData="documentForm"
      :invoiceIssuerOptions="invoiceIssuerOptions"
      :selectedInvoiceIssuer="selectedInvoiceIssuer"
      :issuerSearch="issuerSearch"
      :menu="menu"
      @save-document="saveDocument"
      @close-document="closeDocumentDialog"
      @create-new-invoice-issuer="createNewInvoiceIssuer" />

    <!-- Dialog: Dokument-Vorschau & Aktionen -->
    <document-actions-dialog
      :dialog="documentActionsDialog"
      :document="currentDocument"
      :previewBlobUrl="previewBlobUrl"
      :previewHtml="previewHtml"
      :previewHeight="previewHeight"
      :isResizing="isResizing"
      :mailDetails="mailDetails"
      :activeTab="activeTab"
      :newComment="newComment"
      :newAssignedUser="newAssignedUser"
      :newAssignedUserRights="newAssignedUserRights"
      @close="closeDocumentActionsDialog"
      @download-document="downloadDocument"
      @edit-document="openEditDocumentDialog"
      @start-resize="startResize"
      @resize-preview="resizePreview"
      @stop-resize="stopResize"
      @decide-assignment="decideAssignment"
      @update-new-comment="newComment = $event"
      @update-new-assigned-user="updateNewAssignedUser"
      @add-assigned-user="addAssignedUser"
      @remove-assigned-user="removeAssignedUser"
      @update:activeTab="activeTab = $event" />
  </div>
</template>

<script>
import FolderTree from "./components/folderTree.vue"
import userService from "@/services/userService"
import DocumentList from "./components/documentList.vue"
import FolderDialog from "./components/folderDialog.vue"
import DeleteFolderDialog from "./components/deleteFolderDialog.vue"
import DocumentDialog from "./components/documentDialog.vue"
import mammoth from "mammoth"
import { mapState, mapActions, mapGetters } from "vuex"
import DocumentActionsDialog from "./components/documentActionsDialog.vue"
import api from "@/api"

export default {
  name: "FolderTreeManager",
  components: {
    FolderTree,
    DocumentList,
    FolderDialog,
    DeleteFolderDialog,
    DocumentDialog,
    DocumentActionsDialog,
  },
  data() {
    return {
      drawer: false,
      userService,
      loading: false,
      folders: [],
      folderTree: [],
      documents: [],
      selectedFolder: null,
      searchQuery: "",
      documentHeaders: [
        { text: "Datum", value: "date" },
        { text: "Status", value: "approvalStatus" },
        { text: "Titel", value: "title" },
        { text: "Rechnungssteller", value: "invoiceIssuer" },
        { text: "Zugewiesen", value: "assignedUsers" }, // NEU
      ],
      // Zustände und Formulare für Ordner
      folderDialog: false,
      folderDialogMode: "create",
      folderForm: {
        _id: null,
        name: "",
        parent: null,
      },
      deleteFolderDialog: false,
      folderToDelete: {},
      // Zustände und Formulare für Dokumente
      documentDialog: false,
      selectedInvoiceIssuer: null,
      invoiceIssuerOptions: [],
      documentForm: {
        _id: null,
        originalFileName: "",
        title: "",
        description: "",
        invoiceIssuer: null,
        amount: null,
        invoiceNumber: "",
        date: null,
        dueDate: null,
        category: null,
        studio: null,
      },
      menu: false,
      documentActionsDialog: false,
      currentDocument: {},
      newComment: "",
      previewBlobUrl: null,
      previewHtml: null,
      previewHeight: Math.round(window.innerHeight * 0.2),
      isResizing: false,
      startY: 0,
      startHeight: 600,
      mailDetails: null,
      activeTab: 0,
      // Für Assigned Users
      newAssignedUser: null,
      newAssignedUserRights: "manage",
      // Variablen für v-treeview
      openNodes: [],
      dragOverTimeout: null,
      issuerSearch: "",
      selectedStudioId: null,
      userStudio: null,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    mappedStudios() {
      let studios = this.storeStudios
        .filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
        .map((studio) => {
          return { _id: studio._id, name: studio.name }
        })

      studios.unshift({ _id: null, name: "Alle Studios" })

      return studios
    },
    folderTreeWithInbox() {
      return [
        { _id: "inbox", name: "Eingang", children: [] },
        { _id: "notSigned", name: "Noch nicht visiert", children: [] },
        { _id: "signed", name: "Visiert", children: [] },
        { _id: "declined", name: "Abgelehnt", children: [] },
        ...this.folderTree,
      ]
    },
    activeFolderId() {
      return this.selectedFolder ? this.selectedFolder._id : "inbox"
    },
    parentFolderOptions() {
      return this.folders.filter((f) => !this.folderForm._id || f._id !== this.folderForm._id)
    },
    isApp() {
      return window.flutter_inappwebview || false
    },
  },
  methods: {
    toggleDrawer() {
      this.drawer = !this.drawer
    },
    async decideAssignment(status) {
      try {
        if (status.assignUser) {
          const assignment = { user: status.assignUser, rights: "manage" }
          await api.documents.assignDocument(this.currentDocument._id, { assignment })
        }

        const payload = { status: status.status, comment: status.comment }
        const updatedDocument = await api.documents.decideDocument(this.currentDocument._id, payload)
        this.currentDocument = updatedDocument
        this.newComment = ""
        if (status.status === "accepted") this.closeDocumentActionsDialog()
      } catch (error) {
        console.error("Fehler bei der Entscheidung:", error)
      }
    },
    async fetchInvoiceIssuers() {
      try {
        const data = await api.invoiceIssuers.fetch()
        this.invoiceIssuerOptions = data
      } catch (err) {
        console.error("Fehler beim Abrufen der Rechnungssteller:", err)
      }
    },
    async fetchFolders() {
      try {
        const data = await api.documents.fetchFolders()
        this.folders = data
        this.buildFolderTree()
      } catch (error) {
        console.error("Fehler beim Abrufen der Ordner:", error)
      }
    },
    buildFolderTree() {
      const map = {}
      this.folders.forEach((folder) => {
        folder.children = []
        map[folder._id] = folder
      })
      const tree = []
      this.folders.forEach((folder) => {
        if (folder.parent) {
          if (map[folder.parent]) {
            map[folder.parent].children.push(folder)
          }
        } else {
          tree.push(folder)
        }
      })
      this.folderTree = tree
    },
    sortData(data) {
      return data.sort((a, b) => {
        // Die Bedingung "Muss *ich* noch bearbeiten?" anpassen
        const aCondition = a.userIsAssigned && a.approvalStatus !== "signed" && a.currentUserStatus !== "accepted"
        const bCondition = b.userIsAssigned && b.approvalStatus !== "signed" && b.currentUserStatus !== "accepted"

        if (aCondition === bCondition) return 0
        return aCondition ? -1 : 1
      })
    },
    async fetchDocuments(folderId = null) {
      this.loading = true
      try {
        const params = {}
        if (this.searchQuery.trim() !== "") {
          // Bei Suche global über alle Ordner hinweg

          params.q = this.searchQuery

          params.studio = this.selectedStudioId

          const data = await api.documents.searchDocuments(params)

          this.documents = this.sortData(data)
        } else {
          // Ohne Suchbegriff wird der Ordnerfilter angewendet
          if (folderId && folderId !== "inbox") {
            params.folderId = folderId
          } else {
            params.folderId = "inbox"
          }

          params.studio = this.selectedStudioId

          const data = await api.documents.fetchDocuments(params)
          this.documents = this.sortData(data)
        }
      } catch (error) {
        console.log("Fehler beim Abrufen der Dokumente:", error)
      } finally {
        this.loading = false
      }
    },
    findFolderById(folders, id) {
      for (const folder of folders) {
        if (folder._id === id) {
          return folder
        }
        if (folder.children && folder.children.length > 0) {
          const found = this.findFolderById(folder.children, id)
          if (found) {
            return found
          }
        }
      }
      return null
    },
    onFolderSelect(folderId) {
      if (!folderId) return

      const folder = this.findFolderById(this.folderTreeWithInbox, folderId)
      if (!folder) return

      const hadSearch = this.searchQuery.trim() !== ""
      const folderChanged = !this.selectedFolder || this.selectedFolder._id !== folderId
      this.searchQuery = ""

      if (folderChanged || hadSearch) {
        this.selectFolder(folder)
      }

      if (this.drawer) {
        this.$nextTick(() => {
          this.drawer = false
        })
      }
    },
    selectFolder(folder) {
      this.selectedFolder = folder
      this.fetchDocuments(folder._id)
    },
    openCreateFolderDialog() {
      this.folderDialogMode = "create"
      this.folderForm = { _id: null, name: "", parent: null }
      this.folderDialog = true
    },
    openCreateSubfolderDialog(folder) {
      this.folderDialogMode = "create"
      this.folderForm = { _id: null, name: "", parent: folder._id }
      this.folderDialog = true
    },
    openEditFolderDialog(folder) {
      this.folderDialogMode = "edit"
      this.folderForm = { _id: folder._id, name: folder.name, parent: folder.parent }
      this.folderDialog = true
    },
    closeFolderDialog() {
      this.folderDialog = false
    },
    async saveFolder(folderData) {
      try {
        const payload = { name: folderData.name, parent: folderData.parent }
        if (this.folderDialogMode === "create") {
          await api.documents.createFolder(payload)
          await this.fetchFolders()
          if (folderData.parent && !this.openNodes.includes(folderData.parent)) {
            this.openNodes.push(folderData.parent)
          }
        } else {
          const updatedFolder = await api.documents.updateFolder(folderData._id, payload)
          const index = this.folders.findIndex((f) => f._id === folderData._id)
          if (index !== -1) {
            this.folders.splice(index, 1, updatedFolder)
          }
          this.buildFolderTree()
        }
        this.folderDialog = false
      } catch (error) {
        console.error("Fehler beim Speichern des Ordners:", error)
      }
    },
    openDeleteFolderDialog(folder) {
      this.folderToDelete = folder
      this.deleteFolderDialog = true
    },
    async deleteFolder() {
      try {
        await api.documents.deleteFolder(this.folderToDelete._id)
        await this.fetchFolders()
        this.deleteFolderDialog = false
        if (this.selectedFolder && this.selectedFolder._id === this.folderToDelete._id) {
          this.selectedFolder = { _id: "inbox", name: "Eingang" }
          this.fetchDocuments("inbox")
        }
      } catch (error) {
        console.error("Fehler beim Löschen des Ordners:", error)
      }
    },
    searchDocuments() {
      const folderId = this.searchQuery.trim() === "" && this.selectedFolder ? this.selectedFolder._id : undefined
      this.fetchDocuments(folderId)
    },
    downloadDocument(document) {
      console.log(document)
      let title = document.title || document.originalFileName || "Dokument"
      let invoiceNumber = document.invoiceNumber || ""
      let fileName = title

      api.documents.downloadDocument(document._id, fileName)
    },
    openEditDocumentDialog(document) {
      const invoiceIssuerId = document.invoiceIssuer ? document.invoiceIssuer._id : null
      this.documentForm = {
        _id: document._id,
        originalFileName: document.originalFileName || "",
        title: document.title,
        description: document.description,
        category: document.category || null,
        dueDate: document.dueDate || null,
        studio: document.studio || null,
        invoiceIssuer: invoiceIssuerId,
        amount: document.amount,
        invoiceNumber: document.invoiceNumber,
        date: document.date ? this.$moment(document.date).format("YYYY-MM-DD") : null,
      }
      this.selectedInvoiceIssuer = invoiceIssuerId
        ? this.invoiceIssuerOptions.find((issuer) => String(issuer._id) === String(invoiceIssuerId)) || document.invoiceIssuer
        : null
      this.documentDialog = true
    },
    closeDocumentDialog() {
      this.documentDialog = false
    },
    async saveDocument(documentData, invoiceIssuerObj) {
      try {
        let resolvedInvoiceIssuer = invoiceIssuerObj
        if (resolvedInvoiceIssuer && !resolvedInvoiceIssuer._id && resolvedInvoiceIssuer.title) {
          resolvedInvoiceIssuer = await api.invoiceIssuers.create({ title: resolvedInvoiceIssuer.title.trim() })
          if (!this.invoiceIssuerOptions.some((issuer) => issuer._id === resolvedInvoiceIssuer._id)) {
            this.invoiceIssuerOptions.push(resolvedInvoiceIssuer)
          }
          this.selectedInvoiceIssuer = resolvedInvoiceIssuer
        }

        const invoiceIssuerId = resolvedInvoiceIssuer ? resolvedInvoiceIssuer._id : null
        const payload = {
          title: documentData.title,
          description: documentData.description,
          invoiceIssuer: invoiceIssuerId,
          amount: documentData.amount,
          category: documentData.category || null,
          dueDate: documentData.dueDate || null,
          invoiceNumber: documentData.invoiceNumber,
          date: documentData.date,
          studio: documentData.studio,
        }
        const updatedDocument = await api.documents.updateDocument(documentData._id, payload)
        const index = this.documents.findIndex((d) => d._id === documentData._id)
        if (index !== -1) {
          this.documents.splice(index, 1, updatedDocument)
        }
        // Aktualisiere das aktuell geöffnete Dokument
        this.currentDocument.title = documentData.title
        this.currentDocument.description = documentData.description
        this.currentDocument.invoiceIssuer = resolvedInvoiceIssuer || null
        this.currentDocument.amount = documentData.amount
        this.currentDocument.invoiceNumber = documentData.invoiceNumber
        this.currentDocument.date = documentData.date
        this.currentDocument.studio = documentData.studio
        this.currentDocument.category = documentData.category
        this.currentDocument.dueDate = documentData.dueDate
        this.documentDialog = false
      } catch (error) {
        console.error("Fehler beim Speichern des Dokuments:", error)
      }
    },
    onDocumentDragStart(e, document, selectedItems = []) {
      e.dataTransfer.setData("documentId", document._id)
      let ids = []
      if (selectedItems && selectedItems.find((i) => i._id === document._id)) {
        ids = selectedItems.map((i) => i._id)
      } else {
        ids = [document._id]
      }
      e.dataTransfer.setData("documentIds", JSON.stringify(ids))
    },
    onFolderDrop({ folder, documentId, documentIds }) {
      if (documentIds && documentIds.length > 0) {
        this.moveDocumentsToFolder(documentIds, folder._id)
      } else if (documentId) {
        this.moveDocumentToFolder(documentId, folder._id)
      }
    },
    async moveDocumentsToFolder(ids, folderId) {
      if (folderId === "inbox" || folderId === "signed" || folderId === "notSigned" || folderId === "declined") folderId = null

      for (const id of ids) {
        try {
          await api.documents.moveDocument(id, folderId)
        } catch (error) {
          console.error("Fehler beim Verschieben des Dokuments:", id, error)
        }
      }
      const currentFolderId = this.selectedFolder ? this.selectedFolder._id : "inbox"
      this.fetchDocuments(currentFolderId)
    },
    moveDocumentToFolder(documentId, folderId) {
      if (folderId === "inbox" || folderId === "signed" || folderId === "notSigned" || folderId === "declined") folderId = null

      api.documents
        .moveDocument(documentId, folderId)
        .then(() => {
          const currentFolderId = this.selectedFolder ? this.selectedFolder._id : "inbox"
          this.fetchDocuments(currentFolderId)
        })
        .catch((error) => {
          console.error("Fehler beim Verschieben des Dokuments:", error)
        })
    },
    openDocumentActionsDialog(document) {
      if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl)
      this.previewBlobUrl = null
      this.previewHtml = null
      this.mailDetails = null
      this.currentDocument = document
      this.documentActionsDialog = true
      if (!this.isApp) this.loadDocumentPreview(document)
      if (document.mailRef) {
        this.loadDocumentMail(document)
      } else {
        this.mailDetails = null
      }
      this.activeTab = 0
    },
    loadDocumentPreview(document) {
      api.documents
        .previewDocument(document._id)
        .then((blob) => {
          if (!this.documentActionsDialog || !this.currentDocument || this.currentDocument._id !== document._id) return
          if (blob.type === "application/pdf") {
            // Bei PDF: Blob URL erzeugen
            this.previewBlobUrl = URL.createObjectURL(blob)
            this.previewHtml = null
          } else if (blob.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
            // Bei DOCX: Blob in ArrayBuffer umwandeln und mit Mammoth in HTML konvertieren
            blob.arrayBuffer().then((arrayBuffer) => {
              mammoth
                .convertToHtml({ arrayBuffer })
                .then((result) => {
                  if (!this.documentActionsDialog || !this.currentDocument || this.currentDocument._id !== document._id) return
                  this.previewHtml = result.value
                  this.previewBlobUrl = null
                })
                .catch((error) => {
                  console.error("Fehler bei der Konvertierung des DOCX:", error)
                  if (!this.documentActionsDialog || !this.currentDocument || this.currentDocument._id !== document._id) return
                  this.previewHtml = `<p>Fehler bei der Anzeige des Dokuments.</p>`
                  this.previewBlobUrl = null
                })
            })
          } else {
            // Falls das Format nicht unterstutzt wird
            console.warn("Vorschau für dieses Dateiformat wird nicht unterstuetzt.")
            this.previewBlobUrl = null
            this.previewHtml = `<p>Vorschau fuer dieses Dateiformat wird nicht unterstuetzt.</p>`
          }
        })
        .catch((error) => {
          console.error("Fehler beim Laden der Dokument-Vorschau:", error)
          this.previewBlobUrl = null
          this.previewHtml = null
        })
    },
    loadDocumentMail(document) {
      api.documents
        .fetchDocumentMail(document._id)
        .then((mail) => {
          if (!this.documentActionsDialog || !this.currentDocument || this.currentDocument._id !== document._id) return
          this.mailDetails = mail
        })
        .catch((error) => {
          console.error("Fehler beim Laden der Mail-Daten:", error)
          this.mailDetails = null
        })
    },
    closeDocumentActionsDialog() {
      this.documentActionsDialog = false
      if (this.previewBlobUrl) {
        URL.revokeObjectURL(this.previewBlobUrl)
        this.previewBlobUrl = null
      }
      this.previewHtml = null
      this.mailDetails = null
      const folderId = this.selectedFolder ? this.selectedFolder._id : "inbox"
      this.fetchDocuments(folderId)
    },
    startResize(event) {
      this.isResizing = true
      this.startY = event.clientY
      this.startHeight = this.previewHeight
      document.body.style.userSelect = "none"
    },
    resizePreview(event) {
      if (!this.isResizing) return
      const diff = event.clientY - this.startY
      this.previewHeight = Math.max(100, this.startHeight + diff)
    },
    stopResize() {
      this.isResizing = false
      document.body.style.userSelect = ""
    },
    updateNewAssignedUser(id) {
      this.newAssignedUser = id
    },

    addAssignedUser() {
      if (!this.newAssignedUser) return

      const newAssignment = {
        user: this.newAssignedUser,
        rights: this.newAssignedUserRights,
      }

      // Sende nur das neue Assignment ans Backend
      api.documents
        .assignDocument(this.currentDocument._id, { assignment: newAssignment })
        .then((doc) => {
          this.currentDocument = doc
          this.newAssignedUser = null
          this.newAssignedUserRights = "manage"
        })
        .catch((error) => {
          console.error("Fehler beim Hinzufügen des zugewiesenen Benutzers:", error)
        })
    },
    removeAssignedUser(index) {
      // Ermittle die ID des zu entfernenden Benutzers anhand des Indexes
      const userId = this.currentDocument.assignedUsers[index].user._id

      // Rufe den neuen API-Endpunkt auf, um den Benutzer zu entfernen
      api.documents
        .unassignDocument(this.currentDocument._id, [userId])
        .then((doc) => {
          this.currentDocument = doc
        })
        .catch((error) => {
          console.error("Fehler beim Entfernen des Assigned Users:", error)
        })
    },
    handleDragEnter(item) {
      if (item.children && item.children.length > 0 && !this.openNodes.includes(item._id)) {
        this.dragOverTimeout = setTimeout(() => {
          this.openNodes.push(item._id)
        }, 500)
      }
    },
    handleDragLeave(item) {
      if (this.dragOverTimeout) {
        clearTimeout(this.dragOverTimeout)
        this.dragOverTimeout = null
      }
    },
    createNewInvoiceIssuer(title) {
      api.invoiceIssuers
        .create({ title })
        .then((createdIssuer) => {
          this.invoiceIssuerOptions.push(createdIssuer)
          this.selectedInvoiceIssuer = createdIssuer
          this.documentForm.invoiceIssuer = createdIssuer._id
          if (this.currentDocument) {
            this.currentDocument.invoiceIssuer = createdIssuer
          }
          this.issuerSearch = ""
        })
        .catch((err) => {
          console.error("Fehler beim Erstellen des neuen Rechnungsstellers:", err)
        })
    },
    init() {
      this.selectedFolder = null
      this.fetchFolders()
      this.fetchDocuments("inbox")
      this.fetchInvoiceIssuers()
    },
  },
  created() {
    this.userStudio = userService.getStudio()?._id || null

    this.init()
  },
}
</script>

<style scoped>
.document-manager,
.manager-grid {
  min-height: 0;
  background: transparent;
}

.desktop-sidebar {
  flex: 0 0 300px;
  max-width: 300px;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.24), rgba(0, 0, 0, 0.12));
}

.sidebar-content {
  padding: 18px 18px 22px;
}

.drawer-content {
  padding: 22px 18px;
}

.sidebar-heading,
.drawer-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sidebar-heading {
  box-sizing: border-box;
  height: 68px;
  min-height: 68px;
  margin-bottom: 0;
  padding: 0 4px 16px;
}

.drawer-heading {
  min-height: 78px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.34);
}

.sidebar-title-wrap {
  display: flex;
  align-items: center;
  gap: 11px;
}

.sidebar-title-icon {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.34);
  border-radius: 11px;
  background: rgba(0, 150, 64, 0.17);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 8px 18px rgba(0, 0, 0, 0.18);
}

.sidebar-eyebrow {
  color: #65ca8b;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  line-height: 1.2;
  text-transform: uppercase;
}

.sidebar-title {
  margin-top: 2px;
  color: #f2f2f3;
  font-size: 1rem;
  font-weight: 750;
  letter-spacing: -0.01em;
}

.studio-select {
  border-radius: 13px;
}

.studio-select ::v-deep .v-input__slot {
  min-height: 46px !important;
  border-radius: 13px !important;
  background: rgba(255, 255, 255, 0.035);
}

.studio-select ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.14) !important;
}

.studio-select ::v-deep .v-input--is-focused fieldset,
.studio-select.v-input--is-focused ::v-deep fieldset {
  border-color: rgba(0, 150, 64, 0.7) !important;
}

.studio-select ::v-deep .v-label,
.studio-select ::v-deep .v-icon {
  color: #9a9a9f !important;
}

.studio-select ::v-deep .v-select__selection {
  color: #eeeeef !important;
}

.sidebar-hint {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 18px 4px 0;
  padding: 13px;
  border: 1px dashed rgba(255, 255, 255, 0.11);
  border-radius: 13px;
  color: #838388;
  font-size: 0.875rem;
  line-height: 1.45;
}

.manager-grid > .document-content {
  flex: 1 1 0;
  max-width: none;
  min-width: 0;
  padding: 0 20px;
  background: rgba(0, 0, 0, 0.08);
}

.workspace-drawer {
  overflow: hidden;
  border-right: 1px solid rgba(255, 255, 255, 0.11) !important;
  border-top-right-radius: 22px !important;
  border-bottom-right-radius: 22px !important;
  background: rgba(28, 28, 29, 0.96) !important;
  box-shadow: 18px 0 60px rgba(0, 0, 0, 0.42) !important;
  backdrop-filter: blur(20px);
}

.scroll-y {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  scrollbar-width: thin;
}

.drawer-content {
  height: calc(100% - 78px);
  background: radial-gradient(circle at 0 0, rgba(0, 150, 64, 0.065), transparent 30%), rgba(0, 0, 0, 0.16);
}

@media (max-width: 959px) {
  .manager-grid > .document-content {
    height: auto !important;
    min-height: calc(100vh - 260px);
    padding: 0 14px;
  }
}

@media (max-width: 599px) {
  .manager-grid > .document-content {
    padding: 0 10px;
  }

  .workspace-drawer {
    width: min(340px, calc(100vw - 28px)) !important;
  }
}
</style>
